import { memo, useCallback, useRef, useLayoutEffect, useState } from 'react';
import { ProjectDetailsPopover } from '@/components/ProjectDetailsPopover/ProjectDetailsPopover';
import { Project, type ProjectResourceSummary } from '@/api/client';
import { writeProjectChatAttach } from '@/components/Messenger/shared/composer/readChatAttachFromDataTransfer';
import {
  useChatAttachDragSetter,
  getTransparentDragImage,
  CHAT_ATTACH_DRAG_POSITION_MODE,
  CHAT_ATTACH_DRAG_OFFSET_Y_PX,
} from '@/contexts/ChatAttachDragContext';
import type { ProjectType } from '@/api/client';
import type { TableColumn } from '@/hooks/useTableColumns';
import type { ComboConfig } from '@/hooks/useTableColumns';
import { STATUS_BUTTON_GROUP, BUTTON_COLUMNS_GROUP } from '@/hooks/useTableColumns';
import { PROJECT_TYPES, type ProjectTypeValue } from '@/utils/projectTypeColors';
import type { PlannerNameBadgeMode } from '@/hooks/usePlannerNameBadgeMode';
import { getRowBackground, getBaseStatus } from '@/utils/projectStatuses';
import { computeProjectProgress } from '@/utils/progress';
import { ProgressBarWithTooltip } from './ProgressBarWithTooltip';
import { ProjectCell } from './ProjectCell';
import { ProjectTableCellCommentShell } from '@/features/project-cell-comments/ProjectTableCellCommentShell';
import { VirtualRowRemeasureContext } from './VirtualRowRemeasureContext';
import { ProjectRowProjectDetailsContext } from './ProjectRowProjectDetailsContext';
import { useProjectEntity } from '@/hooks/useProjectEntity';
import clsx from 'clsx';

const BASE_COLUMN_IDS = new Set([
  'asta',
  'ase',
  'rin',
  'producer',
  'techeditor',
  'corrector',
  'studio',
  'department',
  'edition',
  'editor',
]);

const CHANGE_HISTORY_COLUMN_ALIASES: Record<string, string> = {
  author: 'authors',
  producer: 'producers',
  techeditor: 'techeditors',
  corrector: 'correctors',
  studio: 'contractors',
  department: 'edition',
};

const CHANGE_HISTORY_COLUMN_IDS = new Set([
  'actions',
  'asta',
  'ase',
  'author_sheets',
  'authors',
  'contractors',
  'corrections_number',
  'deadline',
  'edition',
  'editor',
  'files',
  'has_music',
  'name',
  'no_edits',
  'pages',
  'paper_book_plan',
  'project_approved',
  'project_done',
  'project_dp',
  'project_pdf',
  'project_phonogram',
  'project_type',
  'producers',
  'readers',
  'review_check_type',
  'review_deadline',
  'rights',
  'rin',
  'size',
  'state',
  'techeditors',
  'theme',
  'time',
  'tracks_count',
]);

function resolveChangeHistoryColumnId(columnId: string): string | null {
  const resolved = CHANGE_HISTORY_COLUMN_ALIASES[columnId] ?? columnId;
  return CHANGE_HISTORY_COLUMN_IDS.has(resolved) ? resolved : null;
}

export interface ProjectTableRowProps {
  projectId: string;
  initialProject: Project;
  visibleColumns: TableColumn[];
  combos: Record<string, ComboConfig>;
  atomicOrder: string[];
  onDelete: (id: string) => void;
  onStateChange: (projectId: string, newState: string) => void;
  onProjectTypeChange: (projectId: string, newType: ProjectType | null) => void;
  onOpenSessionsModal?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenNotesPopover?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenFilesPopover?: (project: Project, anchorEl: HTMLElement) => void;
  onOpenReviewCheckEmail?: (project: Project) => void;
  onOpenProductionHandoffEmail?: (project: Project) => void;
  onOpenPreArchiveUploadEmail?: (project: Project) => void;
  onOpenMountingCompleteEmail?: (project: Project) => void;
  onOpenCorrectionsAppliedEmail?: (project: Project) => void;
  onOpenProjectReviewedEmail?: (project: Project) => void;
  isDeleting: boolean;
  typeBadgesEnabled: Record<ProjectTypeValue, boolean>;
  plannerNameBadgeMode: PlannerNameBadgeMode;
  compactReaders: boolean;
  compactStudio: boolean;
  top: number;
  gridTemplateColumns: string;
  draggingColumnIndex: number | null;
  dropIndicatorVisibleIndex: number | null;
  measureRef: (el: HTMLDivElement | null) => void;
  dataIndex: number;
  columnIndexMap: Map<string, number>;
  /** Сводка комментариев к ячейкам: columnId → количество */
  cellCommentSummary?: Record<string, number>;
  /** Сводка заметок / файлов / сессий для бейджей в таблице */
  resourceSummary?: ProjectResourceSummary;
  resourceLoadingByColumn?: Partial<Record<'sessions' | 'notes' | 'files', boolean>>;
  /** К какому краю scrollport липнет колонка «Название» (левый или правый) */
  nameColumnStickyEdge: 'hidden' | 'left' | 'right';
  searchHighlightWords?: string[];
}

function searchHighlightWordsEqual(a?: string[], b?: string[]): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false;
  }
  return true;
}

/** Точечная перерисовка: только строка с новым `project` или изменившимися layout/колонками */
function areProjectTableRowPropsEqual(
  prev: Readonly<ProjectTableRowProps>,
  next: Readonly<ProjectTableRowProps>
): boolean {
  if (prev.projectId !== next.projectId) return false;
  if (prev.initialProject.is_archived !== next.initialProject.is_archived) return false;
  if (prev.top !== next.top) return false;
  if (prev.dataIndex !== next.dataIndex) return false;
  if (prev.gridTemplateColumns !== next.gridTemplateColumns) return false;
  if (prev.draggingColumnIndex !== next.draggingColumnIndex) return false;
  if (prev.dropIndicatorVisibleIndex !== next.dropIndicatorVisibleIndex) return false;
  if (prev.isDeleting !== next.isDeleting) return false;
  if (prev.visibleColumns !== next.visibleColumns) {
    if (prev.visibleColumns.length !== next.visibleColumns.length) return false;
    for (let i = 0; i < prev.visibleColumns.length; i++) {
      const a = prev.visibleColumns[i];
      const b = next.visibleColumns[i];
      if (a.id !== b.id || a.width !== b.width || a.title !== b.title) return false;
    }
  }
  if (prev.combos !== next.combos) return false;
  if (prev.atomicOrder !== next.atomicOrder) {
    if (prev.atomicOrder.length !== next.atomicOrder.length) return false;
    for (let i = 0; i < prev.atomicOrder.length; i++) {
      if (prev.atomicOrder[i] !== next.atomicOrder[i]) return false;
    }
  }
  if (prev.typeBadgesEnabled !== next.typeBadgesEnabled) {
    for (const k of PROJECT_TYPES) {
      if (prev.typeBadgesEnabled[k] !== next.typeBadgesEnabled[k]) return false;
    }
  }
  if (prev.plannerNameBadgeMode !== next.plannerNameBadgeMode) return false;
  if (prev.compactReaders !== next.compactReaders) return false;
  if (prev.compactStudio !== next.compactStudio) return false;
  if (prev.onDelete !== next.onDelete) return false;
  if (prev.onStateChange !== next.onStateChange) return false;
  if (prev.onProjectTypeChange !== next.onProjectTypeChange) return false;
  if (prev.onOpenSessionsModal !== next.onOpenSessionsModal) return false;
  if (prev.onOpenNotesPopover !== next.onOpenNotesPopover) return false;
  if (prev.onOpenFilesPopover !== next.onOpenFilesPopover) return false;
  if (prev.onOpenReviewCheckEmail !== next.onOpenReviewCheckEmail) return false;
  if (prev.onOpenProductionHandoffEmail !== next.onOpenProductionHandoffEmail) return false;
  if (prev.onOpenPreArchiveUploadEmail !== next.onOpenPreArchiveUploadEmail) return false;
  if (prev.onOpenMountingCompleteEmail !== next.onOpenMountingCompleteEmail) return false;
  if (prev.onOpenCorrectionsAppliedEmail !== next.onOpenCorrectionsAppliedEmail) return false;
  if (prev.onOpenProjectReviewedEmail !== next.onOpenProjectReviewedEmail) return false;
  if (prev.measureRef !== next.measureRef) return false;
  if (prev.columnIndexMap !== next.columnIndexMap) {
    if (prev.columnIndexMap.size !== next.columnIndexMap.size) return false;
    for (const [k, v] of prev.columnIndexMap) {
      if (next.columnIndexMap.get(k) !== v) return false;
    }
  }
  if (prev.cellCommentSummary !== next.cellCommentSummary) return false;
  if (prev.resourceSummary !== next.resourceSummary) return false;
  if (
    prev.resourceLoadingByColumn?.sessions !== next.resourceLoadingByColumn?.sessions ||
    prev.resourceLoadingByColumn?.notes !== next.resourceLoadingByColumn?.notes ||
    prev.resourceLoadingByColumn?.files !== next.resourceLoadingByColumn?.files
  ) {
    return false;
  }
  if (prev.nameColumnStickyEdge !== next.nameColumnStickyEdge) return false;
  if (!searchHighlightWordsEqual(prev.searchHighlightWords, next.searchHighlightWords)) return false;
  return true;
}

export const ProjectTableRow = memo(function ProjectTableRow({
  projectId,
  initialProject,
  visibleColumns,
  combos,
  atomicOrder,
  onDelete,
  onStateChange,
  onProjectTypeChange,
  onOpenSessionsModal,
  onOpenNotesPopover,
  onOpenFilesPopover,
  onOpenReviewCheckEmail,
  onOpenProductionHandoffEmail,
  onOpenPreArchiveUploadEmail,
  onOpenMountingCompleteEmail,
  onOpenCorrectionsAppliedEmail,
  onOpenProjectReviewedEmail,
  isDeleting,
  typeBadgesEnabled,
  plannerNameBadgeMode,
  compactReaders,
  compactStudio,
  top,
  gridTemplateColumns,
  draggingColumnIndex,
  dropIndicatorVisibleIndex,
  measureRef,
  dataIndex,
  columnIndexMap,
  cellCommentSummary,
  resourceSummary,
  resourceLoadingByColumn,
  nameColumnStickyEdge,
  searchHighlightWords,
}: ProjectTableRowProps) {
  const project = useProjectEntity(projectId, initialProject) ?? initialProject;
  const [isAttachDragEnabled, setIsAttachDragEnabled] = useState(false);
  /** true между dragstart и dragend — не сбрасывать draggable при mouseLeave во время DnD */
  const chatAttachNativeDragRef = useRef(false);
  const setChatAttachDrag = useChatAttachDragSetter();
  const rowBg = getRowBackground(project.state);
  const isRecording = getBaseStatus(project.state) === 'Запись';
  const isPast = getBaseStatus(project.state) === 'Готов';
  /** Страничный прогресс в таблице не показываем для подкастов */
  const showRecordingProgressInTable = isRecording && project.project_type !== 'Podcast';
  const progress = showRecordingProgressInTable ? computeProjectProgress(project) : 0;

  const elRef = useRef<HTMLDivElement | null>(null);
  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      measureRef(el);
    },
    [measureRef],
  );

  useLayoutEffect(() => {
    if (elRef.current) measureRef(elRef.current);
  }, [dataIndex, measureRef]);

  const remeasureRow = useCallback(() => {
    if (elRef.current) measureRef(elRef.current);
  }, [measureRef]);

  const handlePointerDown = useCallback((e: React.MouseEvent) => {
    setIsAttachDragEnabled(e.ctrlKey || e.metaKey);
  }, []);

  const resetAttachDragModifier = useCallback(() => {
    if (!chatAttachNativeDragRef.current) {
      setIsAttachDragEnabled(false);
    }
  }, []);

  const rowRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    if (!isAttachDragEnabled || (!e.ctrlKey && !e.metaKey)) {
      e.preventDefault();
      return;
    }
    chatAttachNativeDragRef.current = true;
    const payload = writeProjectChatAttach(e.dataTransfer, { id: project.id, name: project.name });

    e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0);

    const rowEl = rowRef.current;
    if (rowEl && setChatAttachDrag) {
      const nameCell = rowEl.querySelector('.projects-table__cell_name') as HTMLElement | null;
      const sourceEl = nameCell ?? rowEl;
      const rect = sourceEl.getBoundingClientRect();
      const clone = sourceEl.cloneNode(true) as HTMLElement;
      Object.assign(clone.style, {
        width: '100%',
        height: '100%',
        minWidth: '0',
      });
      const wrapper = document.createElement('div');
      wrapper.appendChild(clone);
      Object.assign(wrapper.style, {
        position: 'relative',
        left: '0',
        right: 'auto',
        display: 'flex',
        alignItems: 'stretch',
        width: `${rect.width}px`,
        minWidth: `${rect.width}px`,
        height: `${rect.height}px`,
        minHeight: `${rect.height}px`,
        opacity: '1',
        background: rowBg,
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
      });
      wrapper.classList.add('projects-table__drag-ghost');
      setChatAttachDrag({
        type: 'project',
        payload,
        ghostEl: wrapper,
        position: { x: e.clientX, y: e.clientY },
        positionMode: CHAT_ATTACH_DRAG_POSITION_MODE,
        positionOffsetY: CHAT_ATTACH_DRAG_OFFSET_Y_PX,
      });
    }
  }, [isAttachDragEnabled, project.id, project.name, rowBg, setChatAttachDrag]);

  const handleDragEnd = useCallback(() => {
    chatAttachNativeDragRef.current = false;
    setIsAttachDragEnabled(false);
    setChatAttachDrag?.(null);
  }, [setChatAttachDrag]);

  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const projectDetailsAnchorRef = useRef<HTMLElement | null>(null);
  const projectNameCellRef = useRef<HTMLDivElement | null>(null);
  const openProjectDetails = useCallback((anchor: HTMLElement | null) => {
    projectDetailsAnchorRef.current = anchor;
    setProjectDetailsOpen(true);
  }, []);
  const closeProjectDetails = useCallback(() => setProjectDetailsOpen(false), []);

  return (
    <div
      ref={combinedRef}
      data-index={dataIndex}
      data-project-id={projectId}
      className="projects-table__virtual-row projects-table__row-wrapper"
      style={{ top: `${top}px`, transform: 'none' }}
      draggable={isAttachDragEnabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseDown={handlePointerDown}
      onMouseUp={resetAttachDragModifier}
      onMouseLeave={resetAttachDragModifier}
    >
      <ProjectRowProjectDetailsContext.Provider value={openProjectDetails}>
        <VirtualRowRemeasureContext.Provider value={remeasureRow}>
        <div
          ref={rowRef}
          className={clsx(
            'projects-table__row',
            isPast && 'projects-table__row_past',
            project.is_project_highlighted && 'projects-table__row_highlighted',
            project.is_archived && 'projects-table__row_archived'
          )}
          style={{
            gridTemplateColumns,
            backgroundColor: rowBg,
            ['--row-bg' as string]: rowBg,
          }}
        >
          {visibleColumns.flatMap((col, visibleIndex) => {
          const fullIndex = columnIndexMap.get(col.id) ?? -1;
          const isDragging = fullIndex === draggingColumnIndex;
          const showDropBefore = dropIndicatorVisibleIndex === visibleIndex;
          const bookDataHasContent =
            col.id === 'book_data' && !!(project.asta || project.ase || project.rin);
          const performersHasContent =
            col.id === 'performers' &&
            ((project.producers?.length ?? 0) +
              (project.techeditors?.length ?? 0) +
              (project.correctors?.length ?? 0) >
              0);
          const customersHasContent =
            col.id === 'customers' &&
            !!(project.edition_department_code?.trim() || project.edition_name?.trim() || project.editor_name?.trim());
          const rightsHasContent = col.id === 'rights' && !!(project.rights?.trim());
          const themeHasContent = col.id === 'theme' && !!(project.theme?.trim());
          const isBaseColumn = BASE_COLUMN_IDS.has(col.id);
          const isComboColumn = !!(combos[col.id]?.childIds?.length);
          const comboChildIds = isComboColumn ? combos[col.id]?.childIds : undefined;
          const historyColumnIds = comboChildIds
            ?.map(resolveChangeHistoryColumnId)
            .filter((id): id is string => id != null);
          const historyColumnId = historyColumnIds?.length
            ? undefined
            : resolveChangeHistoryColumnId(col.id) ?? undefined;

          const cellClassName = clsx(
            'projects-table__cell',
            col.id === 'name' && 'projects-table__cell_name',
            col.id === 'name' &&
              nameColumnStickyEdge !== 'hidden' &&
              `projects-table__cell_name_sticky projects-table__cell_name_${nameColumnStickyEdge}`,
            col.id === 'name' && showRecordingProgressInTable && 'projects-table__cell_name_has-progress',
            col.id === 'readers' && 'projects-table__cell_readers',
            col.id === 'readers' && compactReaders && 'projects-table__cell_readers--compact',
            col.id === 'author' && 'projects-table__cell_author',
            col.id === 'edition' && 'projects-table__cell_edition',
            col.id === 'editor' && 'projects-table__cell_editor',
            col.id === 'techeditor' && 'projects-table__cell_techeditor',
            col.id === 'corrector' && 'projects-table__cell_techeditor',
            col.id === 'producer' && 'projects-table__cell_readers',
            col.id === 'studio' && 'projects-table__cell_contractors',
            col.id === 'studio' && compactStudio && 'projects-table__cell_contractors--compact',
            col.id === 'performers' && 'projects-table__cell_performers',
            col.id === 'performers' && performersHasContent && 'projects-table__cell_performers_has-content',
            col.id === 'customers' && 'projects-table__cell_customers',
            col.id === 'customers' && customersHasContent && 'projects-table__cell_customers_has-content',
            col.id === 'theme' && 'projects-table__cell_theme',
            col.id === 'theme' && themeHasContent && 'projects-table__cell_theme_has-content',
            col.id === 'rights' && 'projects-table__cell_rights',
            col.id === 'rights' && rightsHasContent && 'projects-table__cell_rights_has-content',
            col.id === 'state' && 'projects-table__cell_state',
            col.id === 'book_data' && 'projects-table__cell_book-data',
            col.id === 'book_data' && bookDataHasContent && 'projects-table__cell_book-data_has-content',
            (col.id === 'deadline' || col.id === 'review_deadline' || col.id === 'review_combo') &&
              'projects-table__cell_deadline',
            col.id === 'review_check_type' &&
              'projects-table__cell_review-field',
            col.id === 'pages' && 'projects-table__cell_pages',
            col.id === 'author_sheets' && 'projects-table__cell_author-sheets',
            col.id === 'time' && 'projects-table__cell_time',
            col.id === 'paper_book_plan' && 'projects-table__cell_paper-book-plan',
            col.id === 'dates' && 'projects-table__cell_dates',
            col.id === 'menu_combo' && 'projects-table__cell_menu-combo',
            (col.id === 'status_flags_combo' || (combos[col.id]?.childIds?.some((id) => BUTTON_COLUMNS_GROUP.has(id)))) &&
              'projects-table__cell_status-flags-combo',
            (col.id === 'sessions' || col.id === 'notes' || col.id === 'files') && 'projects-table__cell_icon',
            (STATUS_BUTTON_GROUP.has(col.id) || combos[col.id]?.childIds?.some((id) => BUTTON_COLUMNS_GROUP.has(id))) &&
              'projects-table__cell_icon',
            isBaseColumn && 'projects-table__cell_base',
            isComboColumn && 'projects-table__cell_combo',
            isDragging && 'projects-table__cell_dragging',
          );

          const result: React.ReactNode[] = [];
          if (showDropBefore) {
            result.push(<div key={`drop-${col.id}`} className="projects-table__drop-indicator-cell" />);
          }
          result.push(
            <div key={col.id} ref={col.id === 'name' ? projectNameCellRef : undefined} className={cellClassName}>
              <ProjectTableCellCommentShell
                projectId={project.id}
                projectName={(project.name ?? '').trim() || 'Проект'}
                columnId={col.id}
                historyColumnId={historyColumnId}
                historyColumnIds={historyColumnIds}
                historyTitle={col.title || (isComboColumn && historyColumnIds?.length ? 'Комбо' : undefined)}
                commentCount={cellCommentSummary?.[col.id] ?? 0}
                showNoEditsBadge={col.id === 'name' && !!project.no_edits}
              >
                <ProjectCell
                  project={project}
                  columnId={col.id}
                  onDelete={onDelete}
                  onStateChange={onStateChange}
                  onProjectTypeChange={onProjectTypeChange}
                  isDeleting={isDeleting}
                  combos={combos}
                  atomicOrder={atomicOrder}
                  onOpenSessionsModal={onOpenSessionsModal}
                  onOpenNotesPopover={onOpenNotesPopover}
                  onOpenFilesPopover={onOpenFilesPopover}
                  onOpenReviewCheckEmail={onOpenReviewCheckEmail}
                  onOpenProductionHandoffEmail={onOpenProductionHandoffEmail}
                  onOpenPreArchiveUploadEmail={onOpenPreArchiveUploadEmail}
                  onOpenMountingCompleteEmail={onOpenMountingCompleteEmail}
                  onOpenCorrectionsAppliedEmail={onOpenCorrectionsAppliedEmail}
                  onOpenProjectReviewedEmail={onOpenProjectReviewedEmail}
                  onOpenProjectDetails={openProjectDetails}
                  typeBadgesEnabled={typeBadgesEnabled}
                  plannerNameBadgeMode={plannerNameBadgeMode}
                  compactReaders={compactReaders}
                  compactStudio={compactStudio}
                  resourceSummary={resourceSummary}
                  resourceLoadingByColumn={resourceLoadingByColumn}
                  searchHighlightWords={searchHighlightWords}
                />
                {col.id === 'name' && showRecordingProgressInTable && (
                  <ProgressBarWithTooltip
                    project={project}
                    progress={progress}
                    plannedSessionsCount={resourceSummary?.plannedSessionCount}
                    detailsAnchorRef={projectNameCellRef}
                    onOpenProjectDetails={openProjectDetails}
                  />
                )}
              </ProjectTableCellCommentShell>
            </div>,
          );
          return result;
        })}
        </div>
        </VirtualRowRemeasureContext.Provider>
      </ProjectRowProjectDetailsContext.Provider>
      <ProjectDetailsPopover
        open={projectDetailsOpen}
        onClose={closeProjectDetails}
        project={project}
        buttonRef={projectDetailsAnchorRef}
      />
    </div>
  );
}, areProjectTableRowPropsEqual);
